import { SettingItem } from "@/types";
import { baseFetch } from "./base";
import { AxiosError } from "axios";
import { ManagerStatisticsData } from "@/types/statistics";
import { AvatarCircleData } from "../types/user";
import { DomainTree, normalizeDomainTree } from "../utils/domainFocus";
import {
  ResponseContextMenuResponse,
  ResponseContextState,
} from "../types/realtimeSpeech";

export const getUserData = async () => {
  try {
    const response = await baseFetch.get("/get_user");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching user data:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const sendUserDT = async (token: string) => {
  try {
    const response = await baseFetch.post("/save-device-token", { token });
    return response.data;
  } catch (error) {
    console.error("Error saving device token:", error);
    throw error;
  }
};

export const getUserConfig = async () => {
  try {
    const response = await baseFetch.get("/config");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching user config:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const changeColorTheme = async (colorTheme: string): Promise<string> => {
  try {
    const response = await baseFetch.post("/change_color_theme", {
      color_theme: colorTheme,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error changing color theme:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};
export const changeMessageTheme = async (
  mesaageTheme: string
): Promise<string> => {
  try {
    const response = await baseFetch.post("/set_lesson_chat_mode", {
      lesson_chat_mode: mesaageTheme,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error changing message theme:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    return "";
  }
};

export const getDomainTree = async (): Promise<DomainTree> => {
  try {
    const response = await baseFetch.get("/domain_tree");
    return normalizeDomainTree(response.data);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching domain tree:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const setDomainFocus = async (
  domain: string,
  focus: string
): Promise<string> => {
  try {
    const response = await baseFetch.post("/set_domain_focus", {
      domain,
      focus,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error setting domain focus:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const getCustomization = async (): Promise<SettingItem[]> => {
  try {
    const response = await baseFetch.get("/customization");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching customization:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const setCustomization = async (
  data: SettingItem[]
): Promise<SettingItem[]> => {
  try {
    const response = await baseFetch.post("/set_customization", {
      customization: data,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error setting customization:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const setBotAvatar = async (botAvatar: string): Promise<string> => {
  try {
    const response = await baseFetch.post("/set_bot_avatar", {
      bot_avatar: botAvatar,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error setting bot avatar:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const uploadUserAvatar = async (avatarFile: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append("file", avatarFile);

    const response = await baseFetch.post("/upload_user_avatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.file_url;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error uploading user avatar:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const sendAudioListeningTime = async (
  userId: string,
  messageId: string,
  duration: number
): Promise<void> => {
  try {
    await baseFetch.post("/usage_statistic", {
      user_id: userId,
      message_id: messageId,
      duration,
    });
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error uploading time listening:", error.message);
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const logoutUser = async (): Promise<string> => {
  try {
    const response = await baseFetch.post("/logout_user");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error logging out user:", error.message);
    } else {
      console.error("Unknown error logging out user:", error);
    }
    throw error;
  }
};

export const passOnboarding = async (): Promise<string> => {
  try {
    const response = await baseFetch.post("/pass_onboarding");
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error passing onboarding:", error.message);
    } else {
      console.error("Unknown error passing onboarding:", error);
    }
    throw error;
  }
};

export const getBrowserData = async (appVersion: string): Promise<string> => {
  try {
    const payload = {
      app_version: appVersion,
    };

    // console.log('payload', payload)

    const response = await baseFetch.post("/browser_data", payload);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error browser_data:", error.message);
    } else {
      console.error("Unknown error browser_data:", error);
    }
    throw error;
  }
};

export const getManagerStatistics =
  async (): Promise<ManagerStatisticsData> => {
    try {
      const response = await baseFetch.get("/is_manager_stat");
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(
          "Axios error fetching manager statistics:",
          error.message
        );

        if (error.response?.status === 401) {
          console.warn(
            "Unauthorized access. You may need to refresh the token."
          );
        }
      } else {
        console.error("Unknown error fetching manager statistics:", error);
      }
      throw error;
    }
  };

export const getAvatarCircle = async (
  user_id: string
): Promise<AvatarCircleData> => {
  try {
    const response = await baseFetch.get("/avatar_circle", {
      params: {
        user_id,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching avatar circle:", error.message);
    } else {
      console.error("Unknown error fetching avatar circle:", error);
    }
    return {
      background_color: null,
    };
  }
};

export const checkSessionStatus = async (
  sid?: string
): Promise<{ status: "active" | "conflict" }> => {
  try {
    const { getSocketInstance } = await import("../utils/socket");
    const socket = getSocketInstance();
    const socketId = socket?.connected ? socket.id : null;

    // Never fall back to clientInstanceId — it is not a socket sid and causes
    // false "conflict" whenever any real sid exists for this user.
    const sidToUse = sid || socketId || undefined;

    const response = await baseFetch.get("/session_status", {
      params: sidToUse ? { sid: sidToUse } : {},
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error checking session status:", error.message);
    } else {
      console.error("Unknown error checking session status:", error);
    }
    throw error;
  }
};

export const deactivateSessions = async (): Promise<void> => {
  try {
    await baseFetch.post("/sessions_deactivate");
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error deactivating sessions:", error.message);
    } else {
      console.error("Unknown error deactivating sessions:", error);
    }
    throw error;
  }
};

export const getResponseContextMenu =
  async (): Promise<ResponseContextMenuResponse> => {
    try {
      const response = await baseFetch.get("/response_context_menu");
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(
          "Axios error fetching response context menu:",
          error.message,
        );
      } else {
        console.error("Unknown error fetching response context menu:", error);
      }
      throw error;
    }
  };

export const setResponseContext = async (
  data: ResponseContextState,
): Promise<ResponseContextState> => {
  try {
    const response = await baseFetch.post("/set_response_context", data);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error setting response context:", error.message);
    } else {
      console.error("Unknown error setting response context:", error);
    }
    throw error;
  }
};
